<template>

    <div>

        <v-action-modals :action="action" />

        <transition name="modal">
            <v-modal-campaign-delete v-if="showModal === 'delete-' + campaign.id" :campaign="campaign" @deleted="$emit('deleted')" />
            <v-modal-campaign-duplicate v-if="showModal === 'duplicate-' + campaign.id" :campaign="campaign" @duplicated="$emit('duplicated')" />
        </transition>

        <div class="campaign">
            <div class="campaign-body">
                <div class="actions">
                    <template v-if="visibleActions.length">
                        <div v-for="(action, index) in visibleActions" :key="action.id" class="action" :title="action.type">
                            <a class="icon" href="#" @click.prevent="editAction(action)">
                                <div v-if="action.incomplete" class="incomplete">
                                    <i class="fas fa-exclamation-triangle text-danger fs-11"></i>
                                </div>
                                <v-icon :symbol="'action-' + action.type" :size="40" :radius="3" />
                            </a>
                            <i v-if="index < visibleActions.length - 1" class="arrow fas fa-arrow-right"></i>
                        </div>
                    </template>
                    <template v-else>
                        <div v-for="index in 3" :key="index" class="action">
                            <div class="placeholder"></div>
                            <i v-if="index < 3" class="arrow fas fa-arrow-right"></i>
                        </div>
                    </template>
                    <template v-if="actions.length - visibleActions.length > 0">
                        <div class="action">
                            <i class="arrow fas fa-arrow-right"></i>
                            <v-button size="sm" btn-style="secondary" @click.native="editCampaign">{{ (actions.length - visibleActions.length) | numeral }} More</v-button>
                        </div>
                    </template>
                </div>
                <v-button-toolbar size="sm">
                    <v-button size="sm" btn-style="secondary" @click.native="editCampaign">Edit</v-button>
                    <v-dropdown
                        type="secondary"
                        label="More"
                        :items="options"
                        :show-selected="false"
                        :menu-right="true"
                        @change="onActionSelected">
                    </v-dropdown>
                </v-button-toolbar>
            </div>
            <div class="campaign-footer">
                <span v-if="campaign.type === 'broadcast'">
                    <span><a href="#" @click.prevent="editCampaign">{{ campaign.name }}</a>&nbsp;&bull;&nbsp;</span>
                    <span v-if="!campaign.is_active" class="text-danger">Draft</span>
                    <span v-else class="text-success">Scheduled <span v-if="campaign.scheduled_at">{{ campaign.scheduled_at | dateTime }}</span></span>
                </span>
                <span v-else>
                    <span><a href="#" @click.prevent="editCampaign">{{ campaign.name }}</a>&nbsp;&bull;&nbsp;</span>
                    <span v-if="!campaign.is_active" class="text-danger">Draft</span>
                    <span v-else class="text-success">Active</span>
                </span>
                <span class="small text-muted">Created {{ campaign.created_at | date }}</span>
            </div>
        </div>

    </div>

</template>

<style scoped lang="scss">

    .campaign-body {
        background: #fff;
        border: 1px solid $color-rule;
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
        display: flex;
        justify-content: space-between;
        padding: 20px;
        .actions {
            align-items: center;
            display: grid;
            grid-auto-flow: column;
            grid-gap: 0;
            .action {
                align-items: center;
                display: flex;
                justify-content: space-between;
            }
            .action .arrow {
                color: $color-muted;
                font-size: 12px;
                margin: 0 5px;
            }
            .action .icon {
                position: relative;
                transition: opacity .3s;
                .incomplete {
                    background: #fff;
                    border-radius: 10px;
                    bottom: 0;
                    display: grid;
                    height: 20px;
                    margin: 0 -10px -10px 0;
                    place-items: center;
                    position: absolute;
                    right: 0;
                    width: 20px;
                    z-index: 1;
                }
                &:hover {
                    opacity: 0.8;
                }
            }
            .action .placeholder {
                background: $color-secondary;
                border-radius: 3px;
                display: grid;
                height: 40px;
                place-items: center;
                width: 40px;
            }
        }
    }

    .campaign-footer {
        align-items: center;
        background: #fff;
        border: 1px solid $color-rule;
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
        border-top: none;
        display: flex;
        justify-content: space-between;
        padding: 15px 20px;
        span {
            @include font-medium(14px);
            line-height: 1.4em;
            &.small {
                font-size: 13px;
            }
        }
        a {
            text-decoration: none;
        }
    }

</style>

<script>

import ActionModals from './ActionModals';
import ModalCampaignDelete from '@modals/campaign/ModalCampaignDelete';
import ModalCampaignDuplicate from '@modals/campaign/ModalCampaignDuplicate';

export default {

    components: {
        'v-action-modals': ActionModals,
        'v-modal-campaign-delete': ModalCampaignDelete,
        'v-modal-campaign-duplicate': ModalCampaignDuplicate,
    },

    props: {
        campaign: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            action: null,
        }
    },

    computed: {

        actions() {
            return this.campaign.actions;
        },

        visibleActions() {
            return this.campaign.actions.slice(0, 10);
        },

        options() {
            if (this.campaign.type === 'broadcast') {
                return [
                    { key: 'messages', value: 'View Messages' },
                    { key: 'duplicate', value: 'Duplicate Broadcast' },
                    { key: 'delete', value: 'Delete Broadcast' },
                ];
            } else {
                return [
                    { key: 'members', value: 'View Members' },
                    { key: 'messages', value: 'View Messages' },
                    { key: 'duplicate', value: 'Duplicate Automation' },
                    { key: 'delete', value: 'Delete Automation' },
                ];
            }
        },

    },

    methods: {

        onActionSelected(item) {
            if (item.key === 'messages') {
                this.viewMessages();
            } else if (item.key === 'members') {
                this.viewMembers();
            } else if (item.key === 'duplicate') {
                this.$store.commit('showModal', 'duplicate-' + this.campaign.id);
            } else if (item.key === 'delete') {
                this.$store.commit('showModal', 'delete-' + this.campaign.id);
            }
        },

        editCampaign() {
            this.$router.push({
                name: this.campaign.type === 'automatic' ? 'automations-edit' : 'broadcasts-edit',
                params: { id: this.campaign.id },
            });
        },

        editAction(action) {
            this.action = action;
            this.$store.commit('campaign/setCampaign', this.campaign);
            this.$store.commit('showModal', `action-edit-${action.type}-` + action.id);
        },

        viewMessages() {
            this.$router.push({
                name: 'campaigns-messages',
                params: { id: this.campaign.id },
            });
        },

        viewMembers() {
            this.$router.push({
                name: 'campaigns-members',
                params: { id: this.campaign.id },
            });
        },

    },

}

</script>
